import React from 'react';

/**
 * Logo - light theme.
 *
 * Renders the official iConnect company logo image (216×72 source bitmap).
 * The `height` prop controls the displayed size - width is computed
 * automatically by the aspect ratio so the logo never distorts.
 *
 * The previous text-wordmark version is kept commented below in case we ever
 * need a typographic fallback (e.g. a print stylesheet or email signature).
 *
 * When a `tagline` string is supplied, the logo image and the tagline are
 * stacked vertically as a single lockup so the tagline always travels with the
 * logo. Without it, the component renders exactly as before (just the <img>),
 * so existing usages (e.g. the Footer) are unaffected.
 */
const Logo = ({ className = '', height = 32, tagline }) => {
  const img = (
    <img
      src={(process.env.PUBLIC_URL || '') + '/iconnect-logo-hd.png'}
      alt="iConnect"
      width={Math.round((height * 1798) / 411)}
      height={height}
      style={{ height, width: 'auto' }}
      className={`inline-block ${tagline ? '' : className}`}
      loading="eager"
      decoding="async"
    />
  );

  if (!tagline) return img;

  // Tagline lockup, styled to mirror the logo wordmark: the first word (e.g.
  // "EDI") takes the brand blue, the rest stays near-black. The line stretches
  // to the full logo width (w-full + justify-between) so it sits under and
  // spans the logo above it, at any logo size.
  const taglineWords = tagline.split(' ');

  return (
    <span className={`inline-flex flex-col items-start ${className}`}>
      {img}
      <span className="mt-1 w-full flex justify-between font-mono font-semibold uppercase text-[10px] leading-none">
        {taglineWords.map((word, i) => (
          <span
            key={i}
            className={i === 0 ? 'text-accent-primary' : 'text-text-primary'}
          >
            {word}
          </span>
        ))}
      </span>
    </span>
  );
};

export default Logo;
